import type { Problem } from './types.ts'

/**
 * Cliente HTTP da API de gestão.
 *
 * Três decisões que o resto da aplicação depende:
 *
 *  1. **O access token vive em memória, nunca em `localStorage`.** O servidor
 *     também o entrega num cookie `HttpOnly`, e essa é a cópia durável. Um XSS
 *     nesta interface não deve virar credencial de longa duração na mão de quem
 *     injetou.
 *  2. **A renovação é enfileirada.** Dez requisições paralelas que tomem 401
 *     disparariam dez refreshes, e a rotação do refresh token mataria nove
 *     delas com `refresh_token_invalid`. Uma renovação por vez; as demais
 *     esperam a mesma promessa.
 *  3. **Reagimos ao 401, não ao relógio.** A validação do token não tem
 *     tolerância de desvio, e o relógio do navegador pode estar errado.
 */

export const API_BASE = '/api/v1'

/**
 * A superfície do CONSOLE. Prefixo separado porque é outra convenção de corpo:
 * /api/v1 responde camelCase (padrão do MVC) e /api/admin responde snake_case
 * explícito. Os dois compartilham autenticação, formato de erro e o teto de
 * paginação — só o nome dos campos difere.
 *
 * ⚠️ As chaves de query string são camelCase nas DUAS superfícies (`?tenantId=`,
 * `?pageSize=`), mesmo que os corpos de resposta de /api/admin sejam snake_case.
 */
export const ADMIN_BASE = '/api/admin'

/** Rotas que não levam Authorization e não disparam renovação. */
const ANONYMOUS = new Set(['/auth/login', '/auth/refresh', '/auth/logout'])

let accessToken: string | null = null
let refreshing: Promise<void> | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function hasAccessToken(): boolean {
  return accessToken !== null
}

// ── Erros ───────────────────────────────────────────────────────────────────

/**
 * Falha de negócio da API.
 *
 * `code` é o que a interface programa em cima; `detail` é texto para humano e
 * pode mudar de redação a qualquer release.
 */
export class AthenaError extends Error {
  readonly code: string
  readonly status: number
  readonly problem: Problem
  /** Erros de binding do framework, quando o 400 vem sem `code`. */
  readonly fieldErrors: Record<string, string[]>

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title ?? 'Falha na requisição.')
    this.name = 'AthenaError'
    this.code = problem.code ?? 'unknown'
    this.status = problem.status
    this.problem = problem
    this.fieldErrors = problem.errors ?? {}
  }

  /** `traceId` só é útil ao operador quando a falha é do servidor. */
  get traceId(): string | null {
    return this.status >= 500 ? (this.problem.traceId ?? null) : null
  }
}

/** A sessão acabou. Quem pega isto manda o usuário para o login, sem retry. */
export class SessionExpiredError extends Error {
  /** `code` do 401 que encerrou a sessão (`company_inactive`, …), quando houve. */
  readonly code: string | null

  constructor(message = 'Sessão expirada.', code: string | null = null) {
    super(message)
    this.name = 'SessionExpiredError'
    this.code = code
  }
}

// ── Aviso de expiração ──────────────────────────────────────────────────────

/** Recebe o `code` do 401 que encerrou a sessão, ou `null` quando não veio um. */
type Listener = (code: string | null) => void
const expiryListeners = new Set<Listener>()

/** O provider de autenticação assina isto para cair na tela de login. */
export function onSessionExpired(listener: Listener): () => void {
  expiryListeners.add(listener)
  return () => expiryListeners.delete(listener)
}

function announceExpiry(code: string | null = null): void {
  accessToken = null
  for (const listener of expiryListeners) listener(code)
}

// ── Leitura da resposta ─────────────────────────────────────────────────────

/**
 * Um `403` chega com **corpo vazio** (o pipeline de autorização responde antes
 * do controller), e um 500 pode chegar em HTML. `response.json()` direto
 * quebraria nos dois casos e trocaria o erro real por um SyntaxError.
 */
async function readProblem(response: Response): Promise<Problem> {
  const text = await response.text().catch(() => '')

  if (text.trim() !== '') {
    try {
      const parsed = JSON.parse(text) as Partial<Problem>
      return {
        ...parsed,
        status: typeof parsed.status === 'number' ? parsed.status : response.status,
        title: typeof parsed.title === 'string' ? parsed.title : response.statusText,
      }
    } catch {
      // Corpo não-JSON: preserva um trecho, que é melhor do que nada no suporte.
      return { status: response.status, title: response.statusText, detail: text.slice(0, 300) }
    }
  }

  return { status: response.status, title: response.statusText }
}

async function readBody<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T
  const text = await response.text()
  if (text.trim() === '') return undefined as T
  return JSON.parse(text) as T
}

// ── Renovação ───────────────────────────────────────────────────────────────

/**
 * Uma renovação por vez. O corpo vai vazio de propósito: o refresh token mora
 * no cookie `athena.refresh` (HttpOnly), que o JavaScript não consegue ler — e
 * é assim que se quer.
 */
function refresh(): Promise<void> {
  refreshing ??= (async () => {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })

    if (!response.ok) {
      // O `code` distingue o corte da empresa (`company_inactive`, §1.5) de um
      // refresh simplesmente vencido: a tela de login diz qual dos dois foi.
      const code = (await readProblem(response)).code ?? null
      announceExpiry(code)
      throw new SessionExpiredError('Sessão expirada.', code)
    }

    const body = await readBody<{ accessToken?: string }>(response)
    if (typeof body?.accessToken !== 'string') {
      announceExpiry()
      throw new SessionExpiredError('Renovação sem token de acesso.')
    }

    accessToken = body.accessToken
  })().finally(() => {
    refreshing = null
  })

  return refreshing
}

/**
 * Renovação sob demanda, para quem não está fazendo uma requisição.
 *
 * Existe por causa do stream: o `EventSource` autentica pelo cookie
 * `athena.jwt`, que vale 15 minutos. Uma conexão já aberta não cai quando o
 * token vence — a autorização acontece na abertura —, mas a reconexão
 * automática depois de qualquer queda levaria 401 e o stream morreria de vez.
 *
 * Reaproveita a MESMA promessa da renovação disparada por um 401: o refresh
 * token rotaciona a cada uso, então duas renovações em paralelo fariam a segunda
 * falhar com `refresh_token_invalid`.
 */
export function renewSession(): Promise<void> {
  return refresh()
}

// ── Chamada ─────────────────────────────────────────────────────────────────

export interface CallOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** Serializado aqui; `undefined` não manda corpo algum. */
  body?: unknown
  signal?: AbortSignal
  /**
   * Prefixo da superfície. Default `API_BASE` (`/api/v1`); o console passa
   * `ADMIN_BASE` (`/api/admin`) e a experiência de cliente `TENANT_BASE`
   * (`/api/tenant`). Só o prefixo muda — autenticação, renovação no
   * 401 e tradução de erro são as mesmas.
   */
  base?: string
}

export async function call<T>(path: string, options: CallOptions = {}): Promise<T> {
  return attempt<T>(path, options, false)
}

async function attempt<T>(path: string, options: CallOptions, retried: boolean): Promise<T> {
  const anonymous = ANONYMOUS.has(path)

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (!anonymous && accessToken !== null) headers['Authorization'] = `Bearer ${accessToken}`

  const init: RequestInit = {
    method: options.method ?? 'GET',
    // Same-origin via proxy: é o cookie que sustenta o SSE, que não aceita header.
    credentials: 'include',
    headers,
  }
  if (options.body !== undefined) init.body = JSON.stringify(options.body)
  if (options.signal !== undefined) init.signal = options.signal

  const response = await fetch(`${options.base ?? API_BASE}${path}`, init)

  // UMA tentativa de renovação. Sem o guarda, um 401 persistente vira laço.
  // O 403 fica de fora de propósito: falta de papel, e renovar não resolve.
  if (response.status === 401 && !retried && !anonymous) {
    await refresh()
    return attempt<T>(path, options, true)
  }

  if (response.status === 401 && !anonymous) {
    announceExpiry()
    throw new SessionExpiredError()
  }

  if (!response.ok) throw new AthenaError(await readProblem(response))

  return readBody<T>(response)
}

// ── Query string ────────────────────────────────────────────────────────────

export type QueryValue = string | number | boolean | undefined | null

/** Omite ausentes e strings vazias: `?search=` filtraria por nada e é ruído. */
export function query(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    search.set(key, String(value))
  }
  const serialised = search.toString()
  return serialised === '' ? '' : `?${serialised}`
}
